/**
 * Persistence schema — Dexie database structure.
 *
 * See spec section 6.2.
 */

import Dexie, { type Table } from "dexie";
import type { Card, Item, Rating, ReviewSource, SessionMix, PlanStop } from "@music-app/learning-system";

/** Stored card record. The card's primary key is the item ID. */
export interface CardRecord {
  id: string;
  card: Card;
  updatedAt: string; // ISO timestamp
}

/** A practice session — recorded for stats and resume support. */
export interface SessionRecord {
  id: string;
  startedAt: string;
  endedAt: string | null; // null while in progress
  itemsReviewed: number;
  correctCount: number;
  averageResponseMs: number;
  focusNodeId: string | null;
  /**
   * Items remaining in the session's queue. Persisted on session start and
   * after every review so a closed tab can be resumed exactly where it
   * left off. Optional for forward-compatibility with records written
   * before this field existed (week 4 #2 and earlier); those are treated
   * as un-resumable.
   */
  queue?: Item[];
  /** Plan stops from the hub that started this session. Absent on records
   *  written before this field existed; resumed sessions without it show
   *  no breadcrumb (degraded gracefully). */
  planStops?: PlanStop[];
}

/** A single review answer — append-only, for diagnostics + future cloud sync. */
export interface ReviewRecord {
  id: string;
  itemId: string;
  sessionId: string;
  rating: Rating;
  correct: boolean;
  responseMs: number;
  timestamp: string;
  /** The option the user selected when correct === false.
   *  Null on correct answers. Absent on records written before this field. */
  chosenAnswerId?: string | null;
  /** Where this review was generated — curriculum practice, a free drill,
   *  or a challenge attempt. Absent on records written before this field;
   *  consumers treat undefined as "practice" for backward compatibility. */
  source?: ReviewSource;
}

/** A user preference — generic key/value. */
export interface PreferenceRecord {
  key: string;
  value: unknown;
  /**
   * ISO timestamp of the last write. Added for cloud sync LWW resolution.
   * Absent on records written before this field existed — treated as epoch
   * by the sync engine (those records lose to any remote write).
   */
  updatedAt?: string;
}

// ── Generalised activity tables ──────────────────────────────────────────────

export type ActivitySessionType = "listen" | "drill" | "practice" | "challenge" | "bypass-quiz";

/** One session of any activity type — replaces listenSessions, drillSessions, challengeAttempts. */
export interface ActivitySessionRecord {
  id: string;
  sessionType: ActivitySessionType;
  /** Primary curriculum node for this session. Null for challenge mixes or unkeyed free drills. */
  nodeId: string | null;
  /** Drill skill slug (e.g. "scales") or challenge key (e.g. "mix-harmony"). */
  skillId: string | null;
  startedAt: string;
  endedAt: string | null;
  /** Total items in the session (set on end). */
  itemCount: number;
  /** Type-specific extras. challenge: { challengeKey, correct, accuracy, averageResponseMs, best }. */
  metadata: Record<string, unknown>;
}

/** One item play within a listen or drill session. */
export interface ItemEventRecord {
  id: string;
  sessionId: string;
  itemId: string;
  /** Curriculum node ID derived from itemId prefix (before "::" separator). */
  nodeId: string;
  /** Drill skill slug — denormalised from session for coverage queries. */
  skillId: string | null;
  eventType: "listen" | "drill";
  playedAt: string;
  metadata: Record<string, unknown>;
}

/** Cumulative stats for one drill combination (scale type × tonic × range × octaves). */
export interface DrillStatsRecord {
  /** Serialized combo key from drillStatsKey(). */
  id: string;
  /** Total rounds started in any drill mode. */
  played: number;
  /** Correct answers in graded mode only. */
  correct: number;
  /** Wrong answers in graded mode only. */
  errors: number;
  /**
   * Sum of per-round accuracy percentages (0–100 each) for dictation-type
   * drills. Divide by `played` to get the rolling average accuracy.
   * Not present on records created before this field was added.
   */
  accuracySum?: number;
  lastPlayedAt: string; // ISO timestamp
}

/** A user-created or app-seeded practice plan. */
export interface SavedPlanRecord {
  id: string;
  name: string;
  source: "app" | "user";
  generatorId: string;
  sessionMix: SessionMix;
  focusTracks: string[] | null;
  focusNodeIds: string[] | null;
  configOverrides: Record<string, unknown> | null;
  generatorState: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export class MusicAppDatabase extends Dexie {
  cards!: Table<CardRecord, string>;
  sessions!: Table<SessionRecord, string>;
  reviews!: Table<ReviewRecord, string>;
  preferences!: Table<PreferenceRecord, string>;
  drillStats!: Table<DrillStatsRecord, string>;
  activitySessions!: Table<ActivitySessionRecord, string>;
  itemEvents!: Table<ItemEventRecord, string>;
  savedPlans!: Table<SavedPlanRecord, string>;

  constructor() {
    super("music-app");
    this.version(1).stores({
      cards: "id, updatedAt",
      sessions: "id, startedAt, endedAt",
      reviews: "id, itemId, sessionId, timestamp",
      preferences: "key",
    });
    this.version(2).stores({
      drillStats: "id",
    });
    // v3 — new tables only; existing tables are preserved automatically
    this.version(3).stores({
      listenSessions: "id, skillId, startedAt",
      challengeAttempts: "id, challengeKey, startedAt",
    });
    // v4 — drill session tracking for daily budget indicator
    this.version(4).stores({
      drillSessions: "id, skillId, startedAt",
    });
    // v5 — unified activity_sessions + item_events (replaces listen/drill/challenge tables)
    this.version(5).stores({
      activitySessions: "id, sessionType, startedAt, nodeId, skillId",
      itemEvents: "id, sessionId, itemId, nodeId, skillId, eventType, playedAt",
    });
    // v6 — drop legacy tables superseded by activity_sessions + item_events
    this.version(6).stores({
      listenSessions: null,
      challengeAttempts: null,
      drillSessions: null,
    });
    // v7 — user-created and app plan library
    this.version(7).stores({
      savedPlans: "id, source, generatorId, updatedAt",
    });
  }
}

/** Singleton database instance. */
let dbInstance: MusicAppDatabase | null = null;
export function getDb(): MusicAppDatabase {
  if (!dbInstance) {
    dbInstance = new MusicAppDatabase();
  }
  return dbInstance;
}

/** For tests — close and reset the singleton. */
export function _resetDbForTesting(): void {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
